import { useState } from 'react';
import type { Deal, TabState } from '../../types';
import { buildSCList, PRODUCT_BUCKETS, bucketProducts, matchesCQFilter } from '../../utils/filter';
import { fmtG } from '../../utils/format';
import { sumUniqueBy, validAges, isAged, ageSeverity, formatAge } from '../../utils/deals';
import { useDeals } from '../../context/DealsContext';
import { MetricCard } from '../ui/MetricCard';
import { MedianAgeCard } from '../ui/MedianAgeCard';
import { GnarrInfluencedCard } from '../ui/GnarrInfluencedCard';
import { SectionHeader } from '../ui/SectionHeader';
import { getURL } from '../../utils/deals';
interface Props {
  state: TabState;
}
function SCMatrix({
  region,
  sub,
  cqState,
  data
}: {
  region: string;
  sub: string;
  cqState: TabState;
  data: Deal[];
}) {
  const ipData = data.filter(d => d.status === 'In Progress' && (region === 'Global' || d.region === region) && (!sub || sub === 'All' || d.sub_region === sub) && matchesCQFilter(d, cqState));
  const COLS = [...PRODUCT_BUCKETS.map(b => b.label), 'Other'];
  const scMap: Record<string, Record<string, number>> = {};
  const seenDR: Record<string, number> = {};
  for (const d of ipData) {
    if (!scMap[d.sc]) scMap[d.sc] = {};
    const bkts = bucketProducts(d.products || '');
    for (const bkt of bkts) {
      const uniq = d.sc + '|' + bkt + '|' + d.id;
      if (seenDR[uniq]) continue;
      seenDR[uniq] = 1;
      scMap[d.sc][bkt] = (scMap[d.sc][bkt] || 0) + 1;
    }
  }
  const scRows = Object.entries(scMap).map(([name, bkts]) => ({
    name: name.split(' ')[0],
    full: name,
    bkts,
    total: Object.values(bkts).reduce((a, b) => a + b, 0)
  })).sort((a, b) => b.total - a.total);
  const colTotals: Record<string, number> = {};
  for (const c of COLS) colTotals[c] = 0;
  for (const row of scRows) {
    for (const c of COLS) colTotals[c] += row.bkts[c] || 0;
  }
  const grandTotal = Object.values(colTotals).reduce((a, b) => a + b, 0);
  const activeCols = COLS.filter(c => colTotals[c] > 0);
  if (scRows.length === 0 || grandTotal === 0) {
    return <tr data-id="element-205">
        <td colSpan={2} style={{
        padding: '12px 10px',
        color: '#8892a0',
        fontSize: 10,
        fontStyle: 'italic'
      }} data-id="element-206">
          No in-progress deals match the current filter.
        </td>
      </tr>;
  }
  return <>
      <thead data-id="element-207">
        <tr data-id="element-208">
          <th data-id="element-209">SC</th>
          {activeCols.map(c => <th key={c} data-id="element-210">{c}</th>)}
          <th data-id="element-211">Total</th>
        </tr>
      </thead>
      <tbody data-id="element-212">
        {scRows.map(row => <tr key={row.full} data-id="element-213">
            <td title={row.full} data-id="element-214">{row.name}</td>
            {activeCols.map(c => <td key={c} data-id="element-215">
                {(row.bkts[c] || 0) > 0 ? <span className="mx-val" data-id="element-216">{row.bkts[c]}</span> : <span style={{
            color: '#d1d5db'
          }} data-id="element-217">—</span>}
              </td>)}
            <td data-id="element-218"><span className="mx-val" style={{
            background: '#e0e7ff',
            color: '#3730a3'
          }} data-id="element-219">{row.total}</span></td>
          </tr>)}
      </tbody>
      <tfoot data-id="element-220">
        <tr data-id="element-221">
          <td data-id="element-222">Total</td>
          {activeCols.map(c => <td key={c} data-id="element-223"><span style={{
            fontWeight: 700,
            color: '#1d4ed8'
          }} data-id="element-224">{colTotals[c] || 0}</span></td>)}
          <td data-id="element-225"><span style={{
            fontWeight: 700,
            color: '#3730a3'
          }} data-id="element-226">{grandTotal}</span></td>
        </tr>
      </tfoot>
    </>;
}
export function ODPanel({
  state
}: Props) {
  const [expandedSC, setExpandedSC] = useState<string | null>(null);
  const {
    deals
  } = useDeals();
  const scData = buildSCList(state.r, state.sub, state, deals);
  const allIP = deals.filter(d => {
    if (d.status !== 'In Progress') return false;
    if (state.r !== 'Global' && d.region !== state.r) return false;
    if (state.sub && state.sub !== 'All' && d.sub_region !== state.sub) return false;
    if (!matchesCQFilter(d, state)) return false;
    return true;
  });
  const scs = new Set(allIP.map(d => d.sc)).size;

  // oppGnarr includes ALL statuses (not just IP), matching region/sub/CQ
  const allForGnarr = deals.filter(d => {
    if (state.r !== 'Global' && d.region !== state.r) return false;
    if (state.sub && state.sub !== 'All' && d.sub_region !== state.sub) return false;
    if (!matchesCQFilter(d, state)) return false;
    return true;
  });
  const oppGnarr = sumUniqueBy(allForGnarr, d => d.opp_gnarr);
  const ages = validAges(allIP);
  const maxIP = Math.max(...scData.map(s => s.ip), 1);
  const maxClosed = Math.max(...scData.map(s => s.closed), 1);
  return <div data-id="element-227">
      {/* Metrics */}
      <div className="mrow" data-id="element-228">
        <MetricCard label="In-progress requests" value={allIP.length} sub={`${scs} SCs active`} colorClass="c-blue" data-id="element-229" />
        <MetricCard label="Requests / person" value={scData.length ? +(allIP.length / scData.length).toFixed(1) : 0} sub="across active SCs" colorClass="c-purple" data-id="element-230" />
        <MedianAgeCard ages={ages} data-id="element-231" />
        <GnarrInfluencedCard value={oppGnarr} colorClass="c-green" data-id="element-232" />
      </div>

      {/* SC workload table */}
      <SectionHeader title="SC workload — deals (click row to expand)" data-id="element-233" />
      <div className="tw" data-id="element-234">
        <table className="tbl" data-id="element-235">
          <thead data-id="element-236">
            <tr data-id="element-237">
              <th style={{
              width: '26%'
            }} data-id="element-238">SC Name</th>
              <th style={{
              width: '18%'
            }} data-id="element-239">In-progress</th>
              <th style={{
              width: '18%'
            }} data-id="element-240">Closed</th>
              <th style={{
              width: '13%'
            }} data-id="element-241">Avg age</th>
              <th style={{
              width: '13%'
            }} data-id="element-242">Median age</th>
              <th style={{
              width: '12%'
            }} data-id="element-243">Book GNARR</th>
            </tr>
          </thead>
          <tbody data-id="element-244">
            {scData.map(p => <>
                <tr key={p.n} onClick={() => setExpandedSC(expandedSC === p.n ? null : p.n)} data-id="element-245">
                  <td data-id="element-246">
                    <span className="name-link" data-id="element-247">
                      {p.n}
                      <span className={`chev${expandedSC === p.n ? ' open' : ''}`} data-id="element-248">▾</span>
                    </span>
                  </td>
                  <td data-id="element-249">
                    <div className="pbg" style={{
                  width: 70
                }} data-id="element-250">
                      <div className="pf" style={{
                    width: `${Math.round(p.ip / maxIP * 100)}%`
                  }} data-id="element-251" />
                    </div>
                    <strong data-id="element-252">{p.ip}</strong>
                  </td>
                  <td data-id="element-253">
                    <div className="pbg" style={{
                  width: 70
                }} data-id="element-254">
                      <div className="pf" style={{
                    width: `${Math.round(p.closed / maxClosed * 100)}%`,
                    background: '#34d399'
                  }} data-id="element-255" />
                    </div>
                    <strong data-id="element-256">{p.closed}</strong>
                  </td>
                  <td style={{
                color: p.avgAge > 90 ? '#f87171' : p.avgAge > 40 ? '#d4a017' : '#344054',
                fontWeight: p.avgAge > 40 ? 600 : 400
              }} data-id="element-257">
                    {p.avgAge}d
                  </td>
                  <td data-id="element-258">{p.med}d</td>
                  <td data-id="element-259">{fmtG(p.gnarr)}</td>
                </tr>
                {expandedSC === p.n && <tr key={`exp-${p.n}`} data-id="element-260">
                    <td colSpan={6} style={{
                padding: 0
              }} data-id="element-261">
                      <div className="exp-inner" data-id="element-262">
                        <div style={{
                    fontSize: 9,
                    fontWeight: 700,
                    color: '#667085',
                    marginBottom: 6,
                    textTransform: 'uppercase',
                    letterSpacing: '.05em'
                  }} data-id="element-263">
                          In-progress book — {p.n} ({p.ip} deals · {fmtG(p.gnarr)} GNARR)
                        </div>
                        <div className="deal-grid" data-id="element-264">
                          {p.deals.map(d => <div key={d.id} className="deal-chip" data-id="element-265">
                              <div className="dc-id" data-id="element-266">{d.id}</div>
                              <div className="dc-name" data-id="element-267">{d.account.substring(0, 35)}</div>
                              <div style={{
                        fontSize: 9,
                        color: '#8892a0',
                        marginTop: 3
                      }} data-id="element-268"><a href={getURL(d)} target="_blank" rel="noopener noreferrer" data-id="element-269">{d.requestName.substring(0, 35)}</a></div>
                              <div className="dc-tags" data-id="element-270">
                                <span className={`badge ${ageSeverity(d.age) === 'high' ? 'b-red' : ageSeverity(d.age) === 'medium' ? 'b-amber' : 'b-gray'}`} data-id="element-271">{formatAge(d.age)}</span>
                                <span className="badge b-blue" data-id="element-272">{fmtG(d.gnarr)}</span>
                                <span className="badge b-purple" style={{
                          fontSize: 8
                        }} data-id="element-273">{d.close_q}</span>
                                {isAged(d) && <span className="badge b-red" data-id="element-274">⚠ aged</span>}
                              </div>
                              <div style={{
                        fontSize: 9,
                        color: '#8892a0',
                        marginTop: 3
                      }} data-id="element-275">
                                {d.sub_region} · {d.offering.substring(0, 26)}
                              </div>
                            </div>)}
                        </div>
                        {p.closedDeals.length > 0 && <>
                            <div style={{
                      fontSize: 9,
                      fontWeight: 700,
                      color: '#059669',
                      margin: '12px 0 6px',
                      textTransform: 'uppercase',
                      letterSpacing: '.05em'
                    }} data-id="element-276">
                              Closed — {p.n} ({p.closed} deals)
                            </div>
                            <div className="deal-grid" data-id="element-277">
                              {p.closedDeals.map(d => <div key={d.id} className="deal-chip" style={{
                        borderColor: '#d1fae5'
                      }} data-id="element-278">
                                  <div className="dc-id" data-id="element-279">{d.id}</div>
                                  <div className="dc-name" data-id="element-280">{d.account.substring(0, 35)}</div>
                                  <div style={{
                          fontSize: 9,
                          color: '#8892a0',
                          marginTop: 3
                        }} data-id="element-281"> <a href={getURL(d)} target="_blank" rel="noopener noreferrer" data-id="element-282">{d.requestName.substring(0, 40)}</a></div>
                                  <div className="dc-tags" data-id="element-283">
                                    <span className="badge" style={{
                            background: '#d1fae5',
                            color: '#065f46'
                          }} data-id="element-284">✓ Closed</span>
                                    <span className="badge b-blue" data-id="element-285">{fmtG(d.gnarr)}</span>
                                    <span className="badge b-purple" style={{
                            fontSize: 8
                          }} data-id="element-286">{d.close_q}</span>
                                  </div>
                                  <div style={{
                          fontSize: 9,
                          color: '#8892a0',
                          marginTop: 3
                        }} data-id="element-287">
                                    {d.sub_region} · {d.offering.substring(0, 26)}
                                  </div>
                                </div>)}
                            </div>
                          </>}
                      </div>
                    </td>
                  </tr>}
              </>)}
          </tbody>
        </table>
      </div>

      {/* Solution × SC matrix */}
      <SectionHeader title="Solution × SC matrix — product concentration" data-id="element-288" />
      <div className="tw" data-id="element-289">
        <div className="sub" data-id="element-290">
          In-progress deals by product bucket per SC. Reveals single-threading and coverage gaps.
        </div>
        <div style={{
        overflowX: 'auto'
      }} data-id="element-291">
          <table className="tbl" id="od-matrix-tbl" style={{
          minWidth: 500
        }} data-id="element-292">
            <SCMatrix region={state.r} sub={state.sub} cqState={state} data={deals} data-id="element-293" />
          </table>
        </div>
      </div>
    </div>;
}